import "./ResponsiveAppBar.css";
import React, { memo, useState } from "react";
import { AppBar, Button } from "@mui/material";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import useWindowDimensions from "@mirage/hooks/useWindowDimensions";
import MobileBottomAppBar from "./bottomResponsiveAppBar/MobileBottomAppBar";
import { UserService } from "@publicPortal/services/UserService";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import { __version } from "../../../../version";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faRectangleHistory, faUser } from "@fortawesome/pro-solid-svg-icons";
import GradientBackground from "../gradientBackground/GradientBackground";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { useRecoilValue } from "recoil";
import { userState } from "@mirage/store/UserStore";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";

const pp = new PublicPortalRoutingService();
const userService = new UserService();
const pagesWithRoutes = [
    {
        page: "My Collection",
        endPoint: pp.CollectedDataRoute(),
    },
    {
        page: "Account",
        endPoint: pp.UserSettingsRoute(),
    },
    {
        page: "Logout",
        endPoint: pp.Root,
    },
] as const;

const ResponsiveAppBar = memo(() => {
    const user = useRecoilValue(userState);
    const { width } = useWindowDimensions();
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const mobileButtons = (
        <>
            <HomeButton />
            <MySearchButton />
            <UserAvatarButton />
        </>
    );
    const desktopRightSideButtons = (
        <>
            <MySearchButton />
            <UserAvatarButton />
        </>
    );

    const showHomeButton = width > PublicPortalTheme.breakpoints.values.md && !consulteeId;

    return (
        <>
            <GradientBackground />
            <AppBar className="custom-app-bar" position="absolute">
                <Container maxWidth="xl">
                    <Toolbar disableGutters className="custom-app-bar__toolbar">
                        {showHomeButton ? (
                            <Box className="app-bar-icons-container">
                                <HomeButton />
                            </Box>
                        ) : null}
                        <Box className="custom-app-bar__toolbar__search-container">
                            <Logo />
                        </Box>
                        {width > PublicPortalTheme.breakpoints.values.md ? <Box className="app-bar-icons-container">{desktopRightSideButtons}</Box> : null}
                    </Toolbar>
                </Container>
            </AppBar>
            {width <= PublicPortalTheme.breakpoints.values.md ? <MobileBottomAppBar>{mobileButtons}</MobileBottomAppBar> : null}
        </>
    );
});

const HomeButton = memo(() => {
    const navigate = useStableNavigate();
    const { isMobile } = useDeviceDetect();
    const iconSize = isMobile ? PublicPortalTheme.icons.sizeM : PublicPortalTheme.icons.sizeL;

    return (
        <Tooltip title="Go to Search">
            <IconButton onClick={() => navigate(pp.Root)} className="icon-button">
                <FontAwesomeIcon icon={faHouse} style={iconSize} />
            </IconButton>
        </Tooltip>
    );
});

const MySearchButton = memo(() => {
    const user = useRecoilValue(userState);
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const navigate = useStableNavigate();
    const { isMobile } = useDeviceDetect();
    const iconSize = isMobile ? PublicPortalTheme.icons.sizeM : PublicPortalTheme.icons.sizeL;

    if (!user || !user.id || consulteeId) {
        return null;
    }
    return (
        <Tooltip title="My Search Collection">
            <IconButton onClick={() => navigate(pp.CollectedDataRoute())} className="icon-button">
                <FontAwesomeIcon icon={faRectangleHistory} style={iconSize} />
            </IconButton>
        </Tooltip>
    );
});

const UserAvatarButton = memo(() => {
    const user = useRecoilValue(userState);
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const navigate = useStableNavigate();
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const { isMobile } = useDeviceDetect();
    const iconSize = isMobile ? PublicPortalTheme.icons.sizeM : PublicPortalTheme.icons.sizeL;
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget);
    const handleCloseUserMenu = async (path: string, page?: string) => {
        setAnchorElUser(null);
        if (page === "Logout") {
            await userService.logout();
            navigate(0);
        } else if (path) {
            navigate(path);
        }
    };
    const getMenuItems = () => {
        const menuItems = pagesWithRoutes.map((data) => {
            if (data.page === "My Collection" && consulteeId) {
                return <div key={data.page}></div>;
            }
            return (
                <MenuItem key={data.page} onClick={async () => await handleCloseUserMenu(data.endPoint, data.page)}>
                    <Typography textAlign="center">{data.page}</Typography>
                </MenuItem>
            );
        });
        getUserMenuHeader(menuItems);
        appendVersionItem(menuItems);

        return menuItems;
    };

    if (!sessionValue.clientSettings?.ENABLE_LOGIN) {
        return <div></div>;
    }
    if (!user || !user.id) {
        return (
            <Button onClick={() => navigate(pp.LoginRoute())} variant="contained" className="sign-in-button">
                Sign In
            </Button>
        );
    }
    return (
        <>
            <Tooltip title={getUserTooltipTitle()}>
                <IconButton onClick={handleOpenUserMenu} className="icon-button">
                    <FontAwesomeIcon icon={faUser} style={iconSize} />
                </IconButton>
            </Tooltip>
            <Menu
                className="menu-container"
                id="menu-appBar"
                MenuListProps={{ className: "menu-container__list-item" }}
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={() => handleCloseUserMenu("")}
            >
                {getMenuItems()}
            </Menu>
        </>
    );

    function getUserMenuHeader(menuItems: JSX.Element[]) {
        menuItems.unshift(
            <li className="user-settings-menu-header-container" key={"user-settings-menu-header-container"}>
                <Typography variant="body1" fontWeight={600}>
                    {user?.first_name} {user?.last_name}
                </Typography>
            </li>
        );
    }

    function appendVersionItem(menuItems: JSX.Element[]) {
        menuItems.push(
            <li className="user-settings-menu-header-container" key={"user-settings-menu-header-container-version"}>
                <Typography variant="body1" fontWeight={100}>
                    Version: {__version}
                </Typography>
            </li>
        );
    }

    function getUserTooltipTitle() {
        if (!user?.first_name && !user?.last_name) {
            return "Account Settings";
        }
        return (
            <div className="user-popper-container">
                <span>
                    {user.first_name || ""} {user.last_name || ""}
                </span>
                <span>Account Settings</span>
            </div>
        );
    }
});

const Logo = memo(() => {
    const [sessionValue] = useSessionStorage(PPConstants.AppName);

    if (!sessionValue.clientSettings?.["logo.png"] && !sessionValue.clientSettings?.["logo.svg"]) {
        console.warn("No logo found in client settings");
        return null;
    }
    let backgroundImage = "";
    if (sessionValue.clientSettings?.["logo.png"]) {
        backgroundImage = `url(data:image/png;base64,${sessionValue.clientSettings?.["logo.png"]})`;
    } else if (sessionValue.clientSettings?.["logo.svg"]) {
        backgroundImage = `url(data:image/svg+xml;base64,${sessionValue.clientSettings?.["logo.svg"]})`;
    }
    if (!backgroundImage) {
        return null;
    }
    if (!sessionValue.clientSettings?.CLIENT_WEBSITE_URL) {
        return <span aria-label="Logo" className="logo" style={{ backgroundImage: backgroundImage }}></span>;
    }
    return (
        <Tooltip title="Go to website">
            <a
                aria-label="Go to website"
                href={sessionValue.clientSettings?.CLIENT_WEBSITE_URL}
                target="_blank"
                className={"logo"}
                style={{ backgroundImage: backgroundImage }}
                rel="noreferrer"
            ></a>
        </Tooltip>
    );
});

export default ResponsiveAppBar;
